import type { ClassOfTrade, IntegrationCategory, IntegrationConfig } from "./types"

export type ConnectTabValue = IntegrationCategory

const CONNECT_TABS: ConnectTabValue[] = ["distributor", "retailer", "accounting", "logistics", "data", "planning"]

export function is_connect_tab_value(value: string): value is ConnectTabValue {
  return CONNECT_TABS.includes(value as ConnectTabValue)
}

/**
 * Categories only MarginWiz staff may see on the connect page.
 *
 * `planning` is here because Vividly is the only thing in it and the TPM sync is still being proven
 * out on one brand — a brand that connected it unprompted would get a promotion calendar rewritten
 * from a system nobody had agreed was the source of truth.
 */
const ADMIN_ONLY_CATEGORIES: ConnectTabValue[] = ["planning"]

export function is_admin_only_connect_tab(tab: ConnectTabValue): boolean {
  return ADMIN_ONLY_CATEGORIES.includes(tab)
}

/** Tabs this user may see, in display order. */
export function visible_connect_tabs(is_system_admin: boolean): ConnectTabValue[] {
  return is_system_admin ? CONNECT_TABS : CONNECT_TABS.filter(tab => !is_admin_only_connect_tab(tab))
}

/**
 * The tab to actually show, given what the URL asked for.
 *
 * Hiding the button is not enough on its own: the tab is also reachable by `#planning` or
 * `?tab=planning`, which is exactly what a shared link from someone who *can* see it looks like.
 * An admin-only tab requested by anyone else falls back to the default rather than rendering.
 */
export function resolve_connect_tab(
  requested: ConnectTabValue | undefined,
  is_system_admin: boolean
): ConnectTabValue {
  if (!requested) {
    return default_connect_tab()
  }
  if (!is_system_admin && is_admin_only_connect_tab(requested)) {
    return default_connect_tab()
  }
  return requested
}

/** Read tab from `?tab=` query param or location hash (hash wins when both present). */
export function parse_connect_tab_from_location(search: string = "", hash: string = ""): ConnectTabValue | undefined {
  const hash_tab = hash.replace(/^#/, "")
  if (is_connect_tab_value(hash_tab)) {
    return hash_tab
  }
  const tab_param = new URLSearchParams(search).get("tab")
  if (tab_param && is_connect_tab_value(tab_param)) {
    return tab_param
  }
  return undefined
}

/**
 * Integrations to show for a search, which reads the whole catalog rather than the active tab.
 *
 * The admin filter has to be applied here as well as to the tabs. Hiding the Planning tab but
 * leaving search over every integration would put the Vividly card one "vividly" away from exactly
 * the people the hidden tab is keeping it from.
 */
export function search_connect_integrations(
  integrations: IntegrationConfig[],
  query: string,
  is_system_admin: boolean
): IntegrationConfig[] {
  const needle = query.toLowerCase()
  return integrations.filter(integration => {
    if (!is_system_admin && is_admin_only_connect_tab(integration.category)) return false
    if (integration.name.toLowerCase().includes(needle)) return true
    if (integration.searchAliases?.some(alias => alias.includes(needle))) return true
    return false
  })
}

/** Section headings on the Distributors tab, in display order. */
const CLASS_OF_TRADE_LABELS: [ClassOfTrade, string][] = [
  ["natural_specialty", "Natural & specialty"],
  ["conventional", "Conventional"],
  ["convenience", "Convenience"],
  ["food_service", "Food service"],
  ["redistributor", "Redistributors"],
  ["dollar", "Dollar channel"],
  ["alcohol", "Alcohol"],
]

export interface ClassOfTradeGroup {
  key: ClassOfTrade | "other"
  label: string
  integrations: IntegrationConfig[]
}

/**
 * The Distributors tab's cards, grouped by class of trade so a brand can scan to its own channel.
 *
 * Order inside a group is the catalog's. A distributor with no class of trade (the catch-all
 * remittance-email reader, a beverage house) goes last under "Other" rather than disappearing, and
 * a group with nothing in it is left out.
 */
export function group_by_class_of_trade(integrations: IntegrationConfig[]): ClassOfTradeGroup[] {
  const groups: ClassOfTradeGroup[] = CLASS_OF_TRADE_LABELS.map(([key, label]: [ClassOfTrade, string]) => ({
    key,
    label,
    integrations: integrations.filter((integration: IntegrationConfig) => integration.classOfTrade === key),
  }))
  groups.push({
    key: "other",
    label: "Other",
    integrations: integrations.filter((integration: IntegrationConfig) => !integration.classOfTrade),
  })
  return groups.filter((group: ClassOfTradeGroup) => group.integrations.length > 0)
}

/** The integration a card connects: its own, or the one it is a name for (`connectsVia`). */
export function connect_target_id(integration: IntegrationConfig): string {
  return integration.connectsVia ?? integration.id
}

/** Link to the connections list, optionally focused on a tab. */
export function connect_list_url(base_url: string, tab?: ConnectTabValue): string {
  return tab ? `${base_url}#${tab}` : base_url
}

/** Link from the connections list into a specific integration setup flow. */
export function connect_integration_url(
  base_url: string,
  integration_id: string,
  tab?: ConnectTabValue
): string {
  return tab ? `${base_url}/${integration_id}?tab=${tab}` : `${base_url}/${integration_id}`
}

/** After connecting, return to the list on the same tab the user started from. */
export function resolve_connect_redirect_to(
  prop_redirect_to?: string,
  default_base_url: string = "/setup/connect"
): string {
  const base = prop_redirect_to ?? default_base_url
  if (typeof window === "undefined") {
    return base
  }
  const tab = parse_connect_tab_from_location(window.location.search, "")
  return connect_list_url(base, tab)
}

export function default_connect_tab(): ConnectTabValue {
  return "distributor"
}

/** Normalize `?tab=retailer` into `#retailer` for shareable list URLs. */
export function normalize_connect_list_location(pathname: string, search: string): string {
  const params = new URLSearchParams(search)
  const query_tab = params.get("tab")
  if (query_tab && is_connect_tab_value(query_tab)) {
    params.delete("tab")
    const remaining_search = params.toString()
    const path = pathname + (remaining_search ? `?${remaining_search}` : "")
    return `${path}#${query_tab}`
  }
  return `${pathname}${search}`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("connect_tab", () => {
    it("builds list URLs with hash tabs", () => {
      expect(connect_list_url("/setup/connect", "retailer")).toBe("/setup/connect#retailer")
      expect(connect_list_url("/settings/connect")).toBe("/settings/connect")
    })

    it("builds integration URLs with tab query param", () => {
      expect(connect_integration_url("/setup/connect", "hyvee", "retailer")).toBe(
        "/setup/connect/hyvee?tab=retailer"
      )
    })

    it("parses tab from hash or query", () => {
      expect(parse_connect_tab_from_location("", "#retailer")).toBe("retailer")
      expect(parse_connect_tab_from_location("?tab=accounting", "")).toBe("accounting")
      expect(parse_connect_tab_from_location("?tab=bad", "#logistics")).toBe("logistics")
    })

    it("recognizes the planning tab", () => {
      // Trade planning systems (Vividly) are the brand's own tooling rather than a counterparty, so
      // they get their own tab. Without this the tab is unroutable and deep links fall back to
      // Distributors.
      expect(is_connect_tab_value("planning")).toBe(true)
      expect(parse_connect_tab_from_location("?tab=planning", "")).toBe("planning")
      expect(connect_integration_url("/settings/connect", "vividly", "planning")).toBe(
        "/settings/connect/vividly?tab=planning"
      )
    })

    it("keeps the planning tab to system admins", () => {
      // Vividly is the only thing in Planning and the TPM sync is still being proven on one brand,
      // so nobody outside the team should stumble into connecting it.
      expect(visible_connect_tabs(true)).toContain("planning")
      expect(visible_connect_tabs(false)).not.toContain("planning")
      // Every other tab is unaffected — this must not quietly narrow the page for brand users.
      expect(visible_connect_tabs(false)).toEqual([
        "distributor",
        "retailer",
        "accounting",
        "logistics",
        "data",
      ])
    })

    it("does not let a shared #planning link render the tab for a brand user", () => {
      // The button being hidden says nothing about the URL, and a link from someone who can see the
      // tab is the likeliest way a brand user arrives on it.
      expect(resolve_connect_tab("planning", false)).toBe("distributor")
      expect(resolve_connect_tab("planning", true)).toBe("planning")
      // A tab nobody gates still resolves for everyone, and no tab means the default.
      expect(resolve_connect_tab("retailer", false)).toBe("retailer")
      expect(resolve_connect_tab(undefined, false)).toBe("distributor")
    })

    it("keeps an admin-only integration out of search results too", () => {
      const catalog = [
        { id: "kehe", name: "KeHE", category: "distributor" },
        { id: "vividly", name: "Vividly", category: "planning", searchAliases: ["govividly", "tpm"] },
      ] as IntegrationConfig[]

      const brand_hits = search_connect_integrations(catalog, "vividly", false).map(i => i.id)
      expect(brand_hits).toEqual([])
      // Not even by one of its aliases.
      expect(search_connect_integrations(catalog, "tpm", false).map(i => i.id)).toEqual([])

      expect(search_connect_integrations(catalog, "vividly", true).map(i => i.id)).toEqual(["vividly"])
      // Everything else is still searchable by both.
      expect(search_connect_integrations(catalog, "kehe", false).map(i => i.id)).toEqual(["kehe"])
    })

    it("groups distributors by class of trade, in order, with the unclassified last", () => {
      const catalog = [
        { id: "email-remittance", name: "Remittance emails", category: "distributor" },
        { id: "core-mark", name: "Core-Mark", category: "distributor", classOfTrade: "convenience" },
        { id: "cands", name: "C&S", category: "distributor", classOfTrade: "conventional" },
        { id: "kehe", name: "KeHE", category: "distributor", classOfTrade: "natural_specialty" },
        { id: "unfi", name: "UNFI", category: "distributor", classOfTrade: "natural_specialty" },
      ] as IntegrationConfig[]

      const groups = group_by_class_of_trade(catalog)
      expect(groups.map(g => g.label)).toEqual(["Natural & specialty", "Conventional", "Convenience", "Other"])
      // Catalog order survives inside a group, and nothing is dropped or shown twice.
      expect(groups[0].integrations.map(i => i.id)).toEqual(["kehe", "unfi"])
      expect(groups[3].integrations.map(i => i.id)).toEqual(["email-remittance"])
      expect(groups.flatMap(g => g.integrations)).toHaveLength(catalog.length)
      // No food service distributor here, so no empty heading for it.
      expect(groups.some(g => g.key === "food_service")).toBe(false)
    })

    it("puts redistributors, the dollar channel and alcohol after the four grocery classes", () => {
      const catalog = [
        { id: "southern-glazer", name: "Southern Glazer's", category: "distributor", classOfTrade: "alcohol" },
        { id: "dollargeneral", name: "Dollar General", category: "distributor", classOfTrade: "dollar" },
        { id: "dotfoods", name: "Dot Foods", category: "distributor", classOfTrade: "redistributor" },
        { id: "gfi", name: "GFI", category: "distributor", classOfTrade: "food_service" },
        { id: "kehe", name: "KeHE", category: "distributor", classOfTrade: "natural_specialty" },
      ] as IntegrationConfig[]

      expect(group_by_class_of_trade(catalog).map(g => g.label)).toEqual([
        "Natural & specialty",
        "Food service",
        "Redistributors",
        "Dollar channel",
        "Alcohol",
      ])
    })

    it("sends a card that is a name for another connection to that connection", () => {
      const mclane = { id: "mclane", name: "McLane", category: "distributor", connectsVia: "email-remittance" } as IntegrationConfig
      const kehe = { id: "kehe", name: "KeHE", category: "distributor" } as IntegrationConfig
      expect(connect_target_id(mclane)).toBe("email-remittance")
      expect(connect_target_id(kehe)).toBe("kehe")
      expect(connect_integration_url("/setup/connect", connect_target_id(mclane), "distributor")).toBe(
        "/setup/connect/email-remittance?tab=distributor"
      )
    })

    it("normalizes query tab into hash", () => {
      expect(normalize_connect_list_location("/setup/connect", "?tab=retailer")).toBe(
        "/setup/connect#retailer"
      )
      expect(normalize_connect_list_location("/setup/connect", "?from=mcp&tab=retailer")).toBe(
        "/setup/connect?from=mcp#retailer"
      )
    })
  })
}
